import { FormControl, FormGroup } from '@angular/forms';
import { SeuAlertBox } from '@seu-types';

export type SeuCalculationStep = 'calculation' | 'summary' | 'detail';

export type SeuCalculationGroup = {
  id: string;
  globalSettingsKey: string;
  interconnectionVersionKey: string;
  operatingConditionsKey: string;
};

export const SEU_CALCULATION_CONTROL_KEYS = [
  'globalSettings',
  'interconnectionVersion',
  'operatingConditions',
] as const;

export type SeuCalculationControlKeys =
  (typeof SEU_CALCULATION_CONTROL_KEYS)[number];

export type SeuComponentSolutionItem = {
  name: string;
  solutionsPerSection: SeuComponentSolutionItem[];
  unitValues: SeuCalculationUnitItem[];
  defaultUnit?: string | null;
};

export type SeuCalculationComponent = {
  componentId: string;
  componentType: string;
  componentInputStreamId: string;
  componentOutputStreamId: string;
  solutions: SeuComponentSolutionItem[];
  codes?: string[];
};

export type SeuCalculationComponentApi = {
  componentId: number;
  componentType: string;
  componentInputStreamId: string;
  componentOutputStreamId: string;
  solutionsPerSection: SeuComponentSolutionItem[];
  codes?: string[];
};

export type SeuCalculationStream = {
  streamId: number;
  canvasStreamId: string;
  generalData: SeuCalculationStreamGroupItem[];
  gasMainComp: SeuCalculationStreamGroupItem[];
  gasSideComp: SeuCalculationStreamGroupItem[];
  otherData: SeuCalculationStreamGroupItem[];
};

type SeuCalculationStreamGroupItem = {
  name: string;
  unitValues: SeuCalculationUnitItem[];
  defaultUnit?: string | null;
  aggregateState?: SeuAggregateState | null;
};

type SeuAggregateState =
  | 'NONE'
  | 'AGGREGATE_STATE_UNSPECIFIED'
  | 'AGGREGATE_STATE_GAS'
  | 'AGGREGATE_STATE_LIQUID'
  | 'AGGREGATE_STATE_SOLID'
  | 'AGGREGATE_STATE_GAS_AND_LIQUID';

export type SeuCalculationUnitItem = {
  unit: string;
  value: number | string | boolean | number[] | string[] | boolean[];
};

export type SeuCalculationPayload = {
  computationInput: SeuComputationInput;
};

type SeuComputationInput = {
  globalParameterSetId: number;
  interconnectionId: number;
  operatingConditionId: number;
};

export type SeuComputationCombination = {
  globalParameterSetId: string | null;
  interconnectionId: string | null;
  operatingConditionIds: string[];
};

export type SeuComputationCombinationApi = {
  globalParameterSetId: number | null;
  interconnectionId: number | null;
  operatingConditionIds: number[];
};

export type SeuActiveCalculation = {
  uid: string;
} & SeuComputationInput;

export type SeuCalculationResponseItem = {
  uid: string;
  globalParameterSetId: string;
  interconnectionId: string;
  computationState: SeuComputationState;
  components: SeuCalculationComponent[];
  streams: SeuCalculationStream[];
  globalCodes: string[];
  opex: SeuCalculationUnitValue | null;
  emissionLimits: SeuEmissionLimitResponse[] | null;
  operatingConditionId: string;
  pollingIntervalSeconds: number;
};

export type SeuComputationState = 'success' | 'warning' | 'critical' | 'error';

export type SeuCalculationResponseApi = SeuCalculationResponseItemApi[];

type SeuEmissionLimitResponse = {
  type: string;
  value: SeuCalculationUnitValue[];
};

export type SeuCalculationResponseItemApi = {
  globalParameterSetId: number;
  interconnectionId: number;
  operatingConditionId: number;
  components: SeuCalculationComponentApi[];
  streams: SeuCalculationStream[];
  globalCodes: string[];
  opex: SeuCalculationUnitValue | null;
  emissionLimits: SeuEmissionLimitResponse[] | null;
  pollingIntervalSeconds: number;
};

export type SeuCalculationReportType =
  | 'error'
  | 'warning'
  | 'info'
  | 'critical';

export type SeuCalculationAlertItem = {
  componentId?: string;
  alertBox: SeuAlertBox;
};

export type SeuCalculationUnitValue = {
  unit: string;
  value: number;
};

export const SEU_CALCULATION_SUCCESS_CODE = 'I000000';

export type SeuFormGroupCalculation = FormGroup<{
  id: FormControl<string | null>;
  globalSettings: FormControl<string | null>;
  interconnectionVersion: FormControl<string | null>;
  operatingConditions: FormControl<string[] | null>;
}>;

export type SeuEmissionLimitTableItem = {
  operator: 'higher' | 'lowerEquals' | null;
  value: string;
};
